import React from 'react';

// material-ui
import { makeStyles, useTheme } from '@mui/styles';
import {
    Avatar,
    Box,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import { useAuth } from 'context/AuthContext';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import SignOut from './SignOut';

// assets
import { IconSettings } from '@tabler/icons';

// style const
const useStyles = makeStyles((theme) => ({
    navContainer: {
        width: '100%',
        maxWidth: '350px',
        minWidth: '300px',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '10px',
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            maxWidth: '100%'
        }
    },
    headerAvatar: {
        cursor: 'pointer',
        ...theme.typography.mediumAvatar,
        margin: '8px 0 8px 8px !important',
        [theme.breakpoints.down('sm')]: {
            width: '40px',
            height: '40px'
        }
    },
    profileChip: {
        height: '48px',
        alignItems: 'center',
        borderRadius: '27px',
        transition: 'all .2s ease-in-out',
        borderColor: theme.palette.primary.light,
        backgroundColor: theme.palette.primary.light,
        [theme.breakpoints.down('sm')]: {
            height: '44px',
            minWidth: '44px'
        },
        '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
                stroke: theme.palette.primary.light
            }
        }
    },
    profileLabel: {
        lineHeight: 0,
        padding: '12px',
        [theme.breakpoints.down('sm')]: {
            padding: '8px'
        }
    },
    listItem: {
        marginTop: '5px'
    },
    cardContent: {
        padding: '16px !important',
        [theme.breakpoints.down('sm')]: {
            padding: '12px !important'
        }
    },
    flex: {
        display: 'flex'
    },
    ScrollHeight: {
        height: '100%',
        maxHeight: 'calc(100vh - 250px)',
        overflowX: 'hidden'
    }
}));

// ===========================|| PROFILE MENU ||=========================== //

const ProfileSection = () => {
    const classes = useStyles();
    const theme = useTheme();
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentUser } = useAuth();

    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);

    const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Signed in user';
    const userEmail = currentUser?.email || 'No email available';
    const userInitial = userName?.trim()?.charAt(0)?.toUpperCase() || 'U';
    const accountLabel = currentUser?.paid ? 'Premium active' : 'Free account';
    const localeLabel = currentUser?.locale ? String(currentUser.locale).toUpperCase() : null;

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);
    return (
        <>
            <Chip
                classes={{ label: classes.profileLabel }}
                className={classes.profileChip}
                label={<IconSettings stroke={1.5} size={matchDownSm ? '1.3rem' : '1.5rem'} color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
                sx={{
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            />
            <Popper
                placement={matchDownSm ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
                sx={{
                    zIndex: theme.zIndex.drawer + 2,
                    width: matchDownSm ? '100%' : 'auto'
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                    sx={{
                                        width: matchDownSm ? '100%' : 'auto',
                                        maxWidth: matchDownSm ? '100%' : '350px'
                                    }}
                                >
                                    <CardContent className={classes.cardContent}>
                                        <Stack spacing={2}>
                                            <Box
                                                sx={{
                                                    p: matchDownSm ? 1.5 : 2,
                                                    borderRadius: 3,
                                                    background:
                                                        'linear-gradient(135deg, rgba(15,140,255,0.12) 0%, rgba(124,58,237,0.12) 62%, rgba(24,201,139,0.1) 100%)',
                                                    border: '1px solid rgba(255,255,255,0.78)'
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar
                                                        src={currentUser?.photoURL || undefined}
                                                        alt={userName}
                                                        sx={{
                                                            width: matchDownSm ? 52 : 60,
                                                            height: matchDownSm ? 52 : 60,
                                                            fontWeight: 900,
                                                            color: '#fff',
                                                            background: 'linear-gradient(135deg, #0066ff 0%, #7c3aed 100%)',
                                                            boxShadow: '0 14px 28px rgba(0, 102, 255, 0.24)'
                                                        }}
                                                    >
                                                        {userInitial}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography
                                                            variant={matchDownSm ? 'h5' : 'h4'}
                                                            sx={{
                                                                fontSize: matchDownSm ? '1.05rem' : '1.25rem',
                                                                lineHeight: 1.2,
                                                                fontWeight: 900,
                                                                color: 'text.primary',
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {userName}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mt: 0.5,
                                                                color: 'text.secondary',
                                                                lineHeight: 1.5,
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {userEmail}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                                                    <Chip
                                                        label={accountLabel}
                                                        size="small"
                                                        color={currentUser?.paid ? 'primary' : 'default'}
                                                        sx={{
                                                            fontWeight: 800
                                                        }}
                                                    />
                                                    {localeLabel && (
                                                        <Chip
                                                            label={localeLabel}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                fontWeight: 800,
                                                                background: 'rgba(255,255,255,0.7)'
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>

                                            <Grid container direction="column" spacing={0}>
                                                <Grid item className={classes.flex}>
                                                    <Typography
                                                        variant={matchDownSm ? 'h6' : 'h5'}
                                                        sx={{
                                                            fontSize: matchDownSm ? '0.95rem' : '1rem',
                                                            lineHeight: 1.4,
                                                            fontWeight: 800,
                                                            color: 'text.primary'
                                                        }}
                                                    >
                                                        Account actions
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 0.5,
                                                            color: 'text.secondary',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        Manage your current session and workspace access.
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Divider />
                                            <PerfectScrollbar className={classes.ScrollHeight}>
                                                <SignOut />
                                            </PerfectScrollbar>
                                        </Stack>
                                    </CardContent>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
